import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Lock as LockIcon, Person as PersonIcon, Edit as EditIcon, Key as KeyIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    fontWeight: 300,
  },
  '& .MuiInputLabel-root': {
    fontWeight: 300,
    color: '#666666',
  },
  '& .MuiFormHelperText-root': {
    fontWeight: 300,
    fontSize: '0.75rem',
  },
};

const UserProfile: React.FC = () => {
  const { user, changePassword, updateProfile } = useAuth();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // --- Estado diálogo editar datos ---
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    surname: '',
    email: '',
    username: '',
  });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const handleOpenEditDialog = () => {
    setEditForm({
      name: user?.name ?? '',
      surname: user?.surname ?? '',
      email: user?.email ?? '',
      username: user?.username ?? '',
    });
    setEditError('');
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditError('');
  };

  const handleSaveProfile = async () => {
    setEditError('');
    if (!editForm.name.trim() || !editForm.username.trim()) {
      setEditError('El nombre y el usuario son obligatorios');
      return;
    }
    setEditLoading(true);
    try {
      await updateProfile({
        name: editForm.name.trim(),
        surname: editForm.surname.trim(),
        email: editForm.email.trim(),
        username: editForm.username.trim(),
      });
      setOpenEditDialog(false);
      showSnackbar('Datos actualizados correctamente');
    } catch (error: any) {
      setEditError(error.message || 'Error al actualizar los datos');
    } finally {
      setEditLoading(false);
    }
  };

  // --- Estado diálogo contraseña ---
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleOpenPasswordDialog = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setPasswordError('');
  };

  const handleChangePassword = async () => {
    setPasswordError('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Todos los campos son obligatorios');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('La nueva contraseña y su confirmación no coinciden');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (passwordForm.newPassword === passwordForm.currentPassword) {
      setPasswordError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setOpenPasswordDialog(false);
      showSnackbar('Contraseña actualizada correctamente');
    } catch (error: any) {
      setPasswordError(error.message || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Cabecera */}
      <Box mb={{ xs: 3, sm: 6 }}>
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 700,
            color: '#001923',
            letterSpacing: '-0.01em',
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
          }}
        >
          Mi Perfil
        </Typography>
        <Typography
          variant="body1"
          className="descripcion-cyan"
          sx={{ fontWeight: 300, lineHeight: 1.6 }}
        >
          Información de tu cuenta
        </Typography>
      </Box>

      {/* Tarjetas */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: { xs: 'stretch', md: 'flex-start' },
        }}
      >
        {/* Tarjeta de datos del usuario */}
        <Paper
          sx={{
            p: { xs: 3, sm: 4 },
            flex: 1,
            border: '1px solid #f0f0f0',
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <PersonIcon sx={{ color: '#00B0BD', fontSize: 22 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: '#001923', fontSize: '0.95rem', flexGrow: 1 }}
            >
              Datos personales
            </Typography>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleOpenEditDialog}
              size="small"
              sx={{
                borderRadius: 0,
                backgroundColor: '#004254',
                color: '#FFFFFF',
                textTransform: 'none',
                fontFamily: 'ForFutureSans, sans-serif',
                fontWeight: 700,
                px: 2,
                '&:hover': { backgroundColor: '#003040' },
              }}
            >
              Editar
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              { label: 'Nombre', value: `${user?.name ?? ''}${user?.surname ? ' ' + user.surname : ''}` },
              { label: 'Usuario', value: user?.username ?? '' },
              { label: 'Email', value: user?.email ?? '—' },
              { label: 'Rol', value: user?.is_admin ? 'Administrador' : 'Usuario' },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 0.25, sm: 0 },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: '#666666',
                    minWidth: 120,
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#001923', fontWeight: 300 }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Tarjeta de seguridad */}
        <Paper
          sx={{
            px: { xs: 3, sm: 4 },
            pt: { xs: 3, sm: 4 },
            pb: { xs: 3, sm: 4 },
            flex: 1,
            border: '1px solid #f0f0f0',
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0 }}>
            <LockIcon sx={{ color: '#00B0BD', fontSize: 22 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: '#001923', fontSize: '0.95rem', flexGrow: 1 }}
            >
              Seguridad
            </Typography>
            <Button
              variant="contained"
              startIcon={<KeyIcon />}
              onClick={handleOpenPasswordDialog}
              size="small"
              sx={{
                borderRadius: 0,
                backgroundColor: '#004254',
                color: '#FFFFFF',
                textTransform: 'none',
                fontFamily: 'ForFutureSans, sans-serif',
                fontWeight: 700,
                px: 2,
                '&:hover': { backgroundColor: '#003040' },
              }}
            >
              Cambiar contraseña
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Diálogo editar datos personales */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '1px solid #f0f0f0',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.1rem',
            fontWeight: 400,
            color: '#1a1a1a',
            borderBottom: '1px solid #f0f0f0',
            pb: 2,
          }}
        >
          Editar datos personales
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ pt: 1 }}>
            {editError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>
                {editError}
              </Alert>
            )}
            <TextField
              fullWidth
              margin="normal"
              label="Nombre"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Apellido"
              value={editForm.surname}
              onChange={(e) => setEditForm({ ...editForm, surname: e.target.value })}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Usuario"
              value={editForm.username}
              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              sx={fieldSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#004254',
              borderColor: '#004254',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#f0f7f9', borderColor: '#004254' },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveProfile}
            disabled={editLoading}
            variant="contained"
            sx={{
              backgroundColor: '#004254',
              color: '#FFFFFF',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#003040' },
              '&.Mui-disabled': { backgroundColor: '#ccc', color: '#fff' },
            }}
          >
            {editLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo cambio de contraseña */}
      <Dialog
        open={openPasswordDialog}
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '1px solid #f0f0f0',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.1rem',
            fontWeight: 400,
            color: '#1a1a1a',
            borderBottom: '1px solid #f0f0f0',
            pb: 2,
          }}
        >
          Cambiar contraseña
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ pt: 1 }}>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>
                {passwordError}
              </Alert>
            )}
            <TextField
              fullWidth
              margin="normal"
              label="Contraseña actual"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Nueva contraseña"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              helperText="Mínimo 6 caracteres"
              sx={fieldSx}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Confirmar nueva contraseña"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              sx={fieldSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={handleClosePasswordDialog}
            variant="outlined"
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#004254',
              borderColor: '#004254',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#f0f7f9', borderColor: '#004254' },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleChangePassword}
            disabled={loading}
            variant="contained"
            sx={{
              backgroundColor: '#004254',
              color: '#FFFFFF',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#003040' },
              '&.Mui-disabled': { backgroundColor: '#ccc', color: '#fff' },
            }}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
